/**
 * About — credits, license, links.
 *
 * Scale-relative layout matching MainMenu's vertical rhythm. Responsive:
 * re-runs the layout on every `scale.resize` so orientation changes
 * and window resizes reflow without leaving stale element positions.
 */
import { Theme } from '../ui/Theme.js';
import { Button } from '../ui/Button.js';
import { SceneBackground } from '../ui/SceneBackground.js';
import { onResize, layoutSlot } from './ResponsiveScene.js';

export class About extends Phaser.Scene {
  constructor() {
    super('About');
  }

  create() {
    this.cameras.main.setBackgroundColor(Theme.colors.bg);
    const slot = layoutSlot();
    onResize(this, () => {
      slot.wipe();
      const bg = SceneBackground.fromRegistry(this);
      if (bg) slot.add(bg);
      this._layout(slot);
    });
  }

  _layout(slot) {
    const reg = this.registry;
    const manifest = reg.get('manifest');
    const i18n = reg.get('i18n');

    const W = this.scale.width;
    const H = this.scale.height;
    const cx = W / 2;

    // Title.
    slot.add(this.add.text(cx, H * 0.13, manifest.name, {
      fontFamily: Theme.font.family,
      fontSize: Theme.font.sizes.xl,
      color: Theme.colors.text,
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 5
    }).setOrigin(0.5));

    // Author + license, just under the title.
    const subtitle = (manifest.author || 'unknown') + '  •  ' + (manifest.license || 'MIT');
    slot.add(this.add.text(cx, H * 0.22, subtitle, {
      fontFamily: Theme.font.family,
      fontSize: Theme.font.sizes.md,
      color: Theme.colors.text,
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0.5));

    // Description (wrapped, with explicit line spacing for readability).
    if (manifest.description) {
      slot.add(this.add.text(cx, H * 0.42, manifest.description, {
        fontFamily: Theme.font.family,
        fontSize: Theme.font.sizes.md,
        color: Theme.colors.text,
        align: 'center',
        wordWrap: { width: W - 100 },
        stroke: '#000000',
        strokeThickness: 3,
        lineSpacing: 4
      }).setOrigin(0.5));
    }

    // Back button (bottom-center).
    const backBtn = new Button({
      scene: this,
      x: cx, y: H * 0.85,
      label: i18n.t('common.back', 'Back'),
      variant: 'secondary',
      onClick: () => this.scene.start('MainMenu')
    });
    slot.add(backBtn.container);

    // Version pinned to the bottom-right corner (matches MainMenu).
    slot.add(this.add.text(W - 12, H - 12, 'v' + manifest.version, {
      fontFamily: Theme.font.family,
      fontSize: Theme.font.sizes.sm,
      color: Theme.colors.text,
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(1, 1));
  }
}
